import { createContext, useContext, ReactNode } from 'react';

/**
 * Shape of the authenticated user stored in localStorage and held in App-level
 * state. Mirrors the JWT payload + any fields hydrated from /auth/me.
 *
 * Exported so other files can type-check against it without duplicating the
 * interface (the previous definition lived privately in App.tsx).
 */
export interface UserData {
  token: string;
  userId?: string;
  email?: string;
  role?: string;
  isActive?: boolean;
  roles?: string[];
  fullName?: string;
  name?: string;
  _computedRoles?: string[];
  personCode?: string;
  employeeId?: string;
  permissions?: string[];
}

const UserContext = createContext<UserData | null>(null);

interface UserProviderProps {
  /** The authenticated user from App-level state, or null while loading / logged out. */
  value: UserData | null;
  children: ReactNode;
}

/**
 * Wrap the authenticated app tree with this provider, passing the user state
 * from App.tsx. All descendant components can then call useCurrentUser()
 * instead of reading localStorage directly.
 */
export function UserProvider({ value, children }: UserProviderProps) {
  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

/**
 * Returns the currently logged-in user, or null if the app is still loading
 * auth state or the user is not authenticated.
 *
 * Usage:
 *   const currentUser = useCurrentUser();
 *   const email = currentUser?.email ?? '';
 *
 * Must be called inside a component that is a descendant of <UserProvider>.
 */
export function useCurrentUser(): UserData | null {
  return useContext(UserContext);
}

export default UserContext;
